import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui";

type ByomRuntime = "ollama" | "vllm" | "lmstudio" | "localai" | "other";
type ProviderType = "builtin_openai" | "byom_openai_compatible";

interface ProviderStatusBadgeProps {
  provider: ProviderType;
  runtime?: ByomRuntime;
  lastTestSuccess?: boolean;
  lastTestedAt?: number;
}

// Custom hook to get runtime labels with translations
function useRuntimeLabels(): Record<ByomRuntime, string> {
  const { t } = useTranslation(["settings"]);
  return {
    ollama: t("settings:aiProvider.runtimeLabels.ollama"),
    vllm: t("settings:aiProvider.runtimeLabels.vllm"),
    lmstudio: t("settings:aiProvider.runtimeLabels.lmstudio"),
    localai: t("settings:aiProvider.runtimeLabels.localai"),
    other: t("settings:aiProvider.runtimeLabels.other"),
  };
}

// Custom hook to get indicator colors with translated labels
function useIndicatorColor(lastTestSuccess?: boolean): {
  bg: string;
  text: string;
  label: string;
} {
  const { t } = useTranslation(["settings"]);

  if (lastTestSuccess === true) {
    return {
      bg: "rgba(34,197,94,0.15)",
      text: "#22c55e",
      label: t("settings:aiProvider.statusBadge.active"),
    };
  }
  if (lastTestSuccess === false) {
    return {
      bg: "rgba(239,68,68,0.15)",
      text: "#ef4444",
      label: t("settings:aiProvider.statusBadge.failed"),
    };
  }
  return {
    bg: "rgba(161,161,166,0.15)",
    text: "#a1a1a6",
    label: t("settings:aiProvider.statusBadge.notTested"),
  };
}

export function ProviderStatusBadge({
  provider,
  runtime,
  lastTestSuccess,
}: ProviderStatusBadgeProps) {
  const { t } = useTranslation("settings");
  const runtimeLabels = useRuntimeLabels();
  const indicator = useIndicatorColor(lastTestSuccess);

  if (provider === "builtin_openai") {
    return (
      <div className="flex items-center gap-2">
        <Badge color={{ bg: "rgba(34,197,94,0.15)", text: "#22c55e" }}>
          {t("aiProvider.statusBadge.active")}
        </Badge>
        <span className="text-[13px] text-secondary">
          {t("aiProvider.statusBadge.builtInOpenAI")}
        </span>
      </div>
    );
  }

  const runtimeLabel = runtime ? runtimeLabels[runtime] : runtimeLabels.other;

  return (
    <div className="flex items-center gap-2">
      <Badge color={{ bg: indicator.bg, text: indicator.text }}>
        {indicator.label}
      </Badge>
      <span className="text-[13px] text-secondary">
        {t("aiProvider.statusBadge.customModel")} ({runtimeLabel})
      </span>
    </div>
  );
}
